import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PENDING_REF_KEY = 'neurowave_pending_ref_code';
// Cached user identity for offline-tolerant auth. We only ever populate this
// after a successful /auth/me round-trip (so the server has confirmed the
// session). On subsequent loads, if /auth/me fails with a *network* error
// (the browser is offline, the API is unreachable), we restore from this
// cache so a returning user can keep using the app on a plane. A real 401
// from the server always wins and clears the cache — we never trust the
// cache over an explicit "no, you're not logged in" response.
const CACHED_USER_KEY = 'neurowave_cached_user_v1';

/**
 * Best-effort referral-code application. Runs every time a user becomes
 * authenticated. If a pending code exists (captured from ?ref= on URL), apply
 * it; the backend quietly rejects duplicate or self-referral uses.
 */
async function tryApplyPendingReferral() {
  let code = null;
  try { code = localStorage.getItem(PENDING_REF_KEY); } catch { /* ignore */ }
  if (!code) return;
  try {
    const resp = await fetch(`${API}/referral/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code }),
    });
    // Always clear the pending code once attempted — one-shot by design.
    try { localStorage.removeItem(PENDING_REF_KEY); } catch { /* ignore */ }
    if (resp.ok) {
      const data = await resp.json();
      // Notify the user their referral bonus was applied
      window.dispatchEvent(new CustomEvent('neurowave:referral-applied', { detail: data }));
      return data;
    }
  } catch { /* ignore — referral is a bonus, not a blocker */ }
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tracks whether the current `user` came from the offline cache rather
  // than a live /auth/me response. Exposed so UI can surface a subtle
  // "offline" indicator if it wants to (no callers wired yet — opt-in).
  const [isOffline, setIsOffline] = useState(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (response.status === 401 || response.status === 403) {
        // Explicit server rejection — wipe the cache so we don't restore a
        // stale identity on the next offline boot.
        try { localStorage.removeItem(CACHED_USER_KEY); } catch { /* ignore */ }
        setUser(null);
        setIsOffline(false);
        return;
      }
      if (!response.ok) throw new Error(`auth-me-${response.status}`);
      const userData = await response.json();
      setUser(userData);
      setIsOffline(false);
      // Persist the confirmed identity for offline fallback. Best-effort —
      // private mode / storage full just means no offline auth for this user.
      try {
        localStorage.setItem(CACHED_USER_KEY, JSON.stringify(userData));
      } catch { /* ignore */ }
      // Apply any stashed ?ref= code (best effort, non-blocking)
      tryApplyPendingReferral();
    } catch {
      // Network failure (offline, DNS failure, etc.) — fall back to the
      // cached user so the app remains usable on a plane. We do NOT clear
      // the cache here, because that would lock the user out for the rest
      // of the session even after they come back online.
      let cached = null;
      try {
        const raw = localStorage.getItem(CACHED_USER_KEY);
        if (raw) cached = JSON.parse(raw);
      } catch { /* ignore */ }
      if (cached && typeof cached === 'object') {
        setUser(cached);
        setIsOffline(true);
      } else {
        setUser(null);
        setIsOffline(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // When the device transitions from offline → online, re-run checkAuth so
  // the offline-cached user gets re-validated against the server (and the
  // isOffline flag clears). No-op on devices that never went offline.
  useEffect(() => {
    const onOnline = () => { checkAuth(); };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch { /* ignore */ }
    // Always clear the offline cache on logout, even if the server call
    // failed — the user's explicit intent is to sign out.
    try { localStorage.removeItem(CACHED_USER_KEY); } catch { /* ignore */ }
    setUser(null);
    setIsOffline(false);
  };

  const setUserData = (userData) => {
    setUser(userData);
    setIsOffline(false);
    // Cache the freshly-set identity for offline fallback (covers the
    // email/password login path that sets user directly without calling /me).
    try {
      if (userData) localStorage.setItem(CACHED_USER_KEY, JSON.stringify(userData));
    } catch { /* ignore */ }
    // Also attempt referral application here — covers the email/password
    // login flow that sets the user directly without calling checkAuth.
    tryApplyPendingReferral();
  };

  return (
    <AuthContext.Provider value={{ user, loading, isOffline, login, logout, setUserData, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
